import { ExternalImagesQuery, ExternalImage } from '@graphql/generated-graphql';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, ExclamationIcon, RefreshIcon, ChevronLeftIcon } from '@heroicons/react/outline';
import NextImage from 'next/image';
import { Button } from '@skipper/ui';
import { FileEntryStatus, useImageModalContext } from './ImageSelectModal';

import { useImageInput } from '@hooks/files/useImageInput';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { EyeIcon, XIcon } from '@heroicons/react/solid';
import { Slider } from '@skipper/ui';
import { useWindowEvent } from '@hooks/useWindowEvent';
import { useFileTypeImage, convertToBytes } from '@components/file/fileUtilities';
import {CircularProgressbar} from "react-circular-progressbar";
import 'react-circular-progressbar/dist/styles.css';

export function aspectRatio(width?: number, height?: number, newWidth = 279, maxHeight = 50) {
    if (!height || !width) {
        return 1;
    }

    const aspectRatio = width / height;
    const finalHeight = aspectRatio * newWidth;
    return Math.max(finalHeight, maxHeight);
}

/**
 * Should return exact Aspect Ratios instead of fractions
 *
 * @param - a
 * @param - b
 * @returns
 */
function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
}

export function useColumnRendering(data: ExternalImagesQuery | undefined) {
    const memoizedColumns = useMemo(() => {
        const columnOne: ExternalImage[] = [];
        const columnTwo: ExternalImage[] = [];
        const columnThree: ExternalImage[] = [];

        data?.externalImages?.forEach((images, index) => {
            const modulo = index % 3;
            switch (modulo) {
                case 0:
                    columnOne.push(images);
                    break;
                case 1:
                    columnTwo.push(images);
                    break;
                case 2:
                    columnThree.push(images);
                    break;
                default:
                    break;
            }
        });

        return { columnOne, columnTwo, columnThree };
    }, [data]);

    return memoizedColumns;
}

export function ImageColumn({
    col,
    setSelected,
}: {
    col: ExternalImage[];
    setSelected: (image: ExternalImage) => void;
}) {
    const urlParser = useCallback((url: string) => {
        const newUrl = new URL(url);
        const searhParams = newUrl?.search?.replace(/w\=\d+/, 'w=1200');
        return `${newUrl.origin}${newUrl.pathname}${searhParams}`;
    }, []);
    return (
        <div className="col-span-1 flex flex-col ">
            {col.map((n) => {
                return (
                    <motion.div
                        key={`${n.url}`}
                        layoutId={`${n.url}`}
                        className="relative overflow-hidden"
                    >
                        <motion.button
                            className="col-span-4 relative overflow-hidden w-[279px] max-w-[279px]"
                            onClick={() => setSelected({ ...n, url: urlParser(n.url) })}
                            initial={false}
                        >
                            <NextImage
                                src={n.url}
                                blurDataURL="/images/default-course-image.jpeg"
                                placeholder="blur"
                                alt={n.description || n.title || 'unsplash image'}
                                layout="responsive"
                                sizes="40vw"
                                width={n.originalWidth || 279}
                                height={n.originalHeight || 279}
                            />
                        </motion.button>
                    </motion.div>
                );
            })}
        </div>
    );
}

export function ImageModalHeader({ text, onClick }: { text: string; onClick: () => void }) {
    return (
        <div className="w-full">
            <div className="relative px-4 py-3 flex items-center justify-center border-b border-gray-200">
                <h1 className="text-xl text-gray-500 font-bold font-larsseit">{text}</h1>
                <button
                    type="button"
                    onClick={onClick}
                    className="absolute px-3 flex items-center justify-center inset-y right-2 focus:outline-primary"
                >
                    <XIcon className="w-5 h-5 fill-gray-500 stroke-gray-500 hover:fill-teal-900 hover:stroke-teal-900 font-bold" />
                </button>
            </div>
        </div>
    );
}

export function ImageModalFooter({ children }: { children?: React.ReactNode }) {
    return <div className="w-full border-t border-gray-200 h-[71px]">{children && children}</div>;
}

export function MultiFileInputView({
    fileList,
    fileCreate,
    selectIcon,
}: {
    fileList: ReturnType<typeof useImageModalContext>['fileList'];
    fileCreate: ReturnType<typeof useImageModalContext>['fileCreate'];
    selectIcon: ReturnType<typeof useFileTypeImage>;
}) {
    return (
        <div className="col-span-6 col-start-2">
            {fileList.map(({ id, status, file, ...rest }) => {
                return (
                    <FileListEntry
                        key={id}
                        status={status}
                        file={file as any}
                        onClick={() => {
                            if (rest?.originalMutate) {
                                fileCreate.mutate(rest?.originalMutate);
                            }
                        }}
                        progress={(rest as any)?.progress}
                        selectIcon={selectIcon}
                    />
                );
            })}
        </div>
    );
}

function FileListEntry<
    FileType extends { fileType?: string; id: string; originalFileSize?: number; title: string },
>({
    file,
    status,
    onClick,
    selectIcon,
    progress
}: {
    file: FileType;
    status: FileEntryStatus;
    onClick?: () => void;
    selectIcon: ReturnType<typeof useFileTypeImage>;
    progress?: number;
}) {
    return (
        <div className="flex py-2 gap-x-2 z-10">
            <div className="relative max-w-[32px]">
                {/* <NextImage src={selectIcon(file?.fileType ?? '')} /> */}
                {selectIcon({
                    type: file?.fileType ?? '',
                    originalFileName: file?.title ?? '',
                })}
            </div>
            <div className="flex flex-col justify-center items-start w-full">
                <p className="text-sm text-passport font-bold">{file?.title}</p>
                <p className="text-xs text-gray-500">
                    {convertToBytes(file?.originalFileSize ?? 0) ?? ''}
                </p>
            </div>
            <div className="flex items-center justify-center">
                {status === FileEntryStatus.Pending && (
                    progress ?
                        <div style={{width: 30, height: 30}}>
                            <CircularProgressbar value={progress} text={`${progress}`} styles={{ text: { fontSize: '35px', fill: '#0199A7' }, path: { stroke: '#0199A7' } }}/>
                        </div> :
                        <div className="transform animate-spin">
                            <RefreshIcon className="w-6 h-6 text-gray-600"/>
                        </div>)}
                {status === FileEntryStatus.Success && (
                    <div>
                        <CheckIcon className="w-6 h-6 text-lime-600"/>
                    </div>
                )}
                {status === FileEntryStatus.Error && (
                    <div className="flex gap-x-4">
                        <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => {
                                onClick?.();
                            }}
                        >
                            <RefreshIcon className="w-6 h-6 text-gray-600" />
                        </Button>
                        <ExclamationIcon className="w-6 h-6 text-red-600" />
                    </div>
                )}
            </div>
        </div>
    );
}

export function ShowSingleFileInput({
    fileInput,
    selectIcon,
    forceType,
}: {
    fileInput: ReturnType<typeof useImageInput>['fileInput'];
    selectIcon: ReturnType<typeof useFileTypeImage>;
    forceType?: string;
}) {
    return (
        <div className="flex py-2 gap-x-2 max-w-[500px]">
            <div className="relative w-[48px]">
                {/* <NextImage src={selectIcon(forceType || fileInput?.file?.type || '')} /> */}
                {selectIcon({
                    type: forceType || fileInput?.file?.type || '',
                    originalFileName: fileInput?.file?.name || '',
                })}
            </div>
            <div className="flex flex-col justify-center items-start w-full">
                <p className="text-sm text-passport font-bold">{fileInput?.file?.name || 'file'}</p>
                <p className="text-xs text-gray-500">
                    {convertToBytes(fileInput?.file?.size ?? 0) ?? ''}
                </p>
            </div>
            <div className="flex items-center justify-center">
                <div>
                    <CheckIcon className="w-6 h-6 text-lime-600" />
                </div>
            </div>
        </div>
    );
}

type TemporaryFile = { file: File | null; url: string };

export function ShowImageFromSrcURL<T extends { url: string; title?: string; file?: File }>({
    croppedArea,
    srcUrl,
    setCrop,
    onClickHeader,
    crop,
    selected,
}: {
    croppedArea: any;
    srcUrl: string;
    setCrop: React.Dispatch<React.SetStateAction<any>>;
    onClickHeader: () => void;
    selected?: T;
    crop: any;
}) {
    const { forcedAspectRatio, cropShape, allowCropping, allowedFileTypes, showImageSearch } =
        useImageModalContext();
    const cropperRef = useRef<any>(null);
    const [cropper, setCropper] = useState<Cropper | null>(null);
    const [previousCrop, setPreviousCrop] = useState(null);
    const [zoom, setZoom] = useState(0);
    const [rotation, setRotation] = useState(0);

    const [containerStyle, setContainerStyle] = useState(() => {
        if (typeof window === 'undefined') return '';

        return window?.innerHeight < 750 ? 'max-h-[250px]' : 'max-h-[400px]';
    });

    const [aspect, setAspect] = useState(
        forcedAspectRatio !== undefined ? eval(forcedAspectRatio) : undefined,
    );

    useWindowEvent('resize', (event) => {
        if (window?.innerHeight < 750) {
            setContainerStyle('max-h-[250px]');
        } else {
            setContainerStyle('max-h-[400px]');
        }
    });

    useEffect(() => {
        if (!forcedAspectRatio) return;

        setAspect(eval(forcedAspectRatio));
    }, [forcedAspectRatio, setAspect]);

    const [croppedFile, setFile] = useState<TemporaryFile>({
        file: null,
        url: '',
    });

    const showPreviewCrop =
        (allowCropping || showImageSearch) &&
        allowedFileTypes !== 'avatar' &&
        allowedFileTypes === 'images';

    if (!!croppedFile?.url) {
        return (
            <div className="flex flex-col h-full">
                <ImageModalHeader onClick={onClickHeader} text="Cropped image preview" />
                <div className="relative grid grid-cols-1 h-full">
                    <div className="col-span-1 flex items-center justify-center">
                        <img
                            src={croppedFile?.url}
                            className="object-contain max-h-[400px] max-w-[500px] bg-transparent"
                        />
                    </div>
                </div>
                <ImageModalFooter>
                    <div className="w-full h-full px-3 flex items-center justify-between py-3">
                        <Button
                            size="large"
                            variant="white"
                            css={{ px: '$3' }}
                            onClick={() => {
                                setFile({ file: null, url: '' });
                            }}
                        >
                            <ChevronLeftIcon className="w-4 h-4 stroke-gray-500 mr-[19px]" />
                            Back
                        </Button>
                        <SaveAndSubmit
                            file={croppedFile}
                            externalImage={selected as any}
                            submitButtonText="Save image"
                        />
                    </div>
                </ImageModalFooter>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <ImageModalHeader onClick={onClickHeader} text="Crop image" />
            <div className="grid grid-cols-2 gap-x-2 p-12 h-full">
                <div
                    className={`relative flex flex-1 col-span-2 max-w-[600px] ${containerStyle} mx-auto`}
                >
                    <ImageCropper
                        ref={cropperRef}
                        cropShape={cropShape}
                        srcUrl={srcUrl}
                        zoom={zoom}
                        aspect={aspect}
                        rotation={rotation}
                        setCropper={setCropper}
                        crop={cropper}
                        setPreviousCrop={setPreviousCrop}
                        previousCrop={previousCrop}
                    />
                </div>
                <div className="col-span-2 mx-auto w-full max-w-[600px]">
                    <Controls
                        zoom={zoom}
                        setZoom={setZoom}
                        rotation={rotation}
                        setRotation={setRotation}
                    />
                </div>
            </div>
            <ImageModalFooter>
                <div className="flex items-center justify-end pr-3 gap-x-3 bg-white h-full py-3">
                    {showPreviewCrop && (
                        <Button
                            size="large"
                            variant="white"
                            onClick={async () => {
                                try {
                                    const file = await new Promise<TemporaryFile>((resolve) => {
                                        if (cropShape === 'circle') {
                                            const canvas = cropper?.getCroppedCanvas({
                                                maxWidth: 4096,
                                                maxHeight: 4096,
                                                fillColor: 'transparent',
                                                imageSmoothingEnabled: false,
                                                imageSmoothingQuality: 'high',
                                            });
                                            getRoundedCrop(canvas)?.toBlob(
                                                (file) => {
                                                    if (!file) return;
                                                    resolve({
                                                        file: new File([file], 'temp', {
                                                            type: 'image/png',
                                                        }),
                                                        url: URL.createObjectURL(file),
                                                    });
                                                },
                                                'image/png',
                                                0.9,
                                            );
                                        } else {
                                            cropper?.getCroppedCanvas()?.toBlob(
                                                (file) => {
                                                    if (!file) return;
                                                    resolve({
                                                        file: new File([file], 'temp', {
                                                            type:
                                                                selected?.file?.type ||
                                                                'image/jpeg',
                                                        }),
                                                        url: URL.createObjectURL(file),
                                                    });
                                                },
                                                selected?.file?.type || 'image/jpeg',
                                                0.9,
                                            );
                                        }
                                    });
                                    setFile(file);
                                } catch (error) {}
                            }}
                        >
                            <EyeIcon className="text-gray-500 w-6 h-6 fill-gray-500 mr-2" />
                            Preview
                        </Button>
                    )}
                    <SaveAndSubmit
                        file={croppedFile}
                        externalImage={selected as any}
                        submitButtonText="Crop and save"
                        getFile={async () => {
                            return new Promise<TemporaryFile>((resolve) => {
                                if (cropShape === 'circle') {
                                    const canvas = cropper?.getCroppedCanvas({
                                        maxWidth: 4096,
                                        maxHeight: 4096,
                                        fillColor: 'transparent',
                                        imageSmoothingEnabled: false,
                                        imageSmoothingQuality: 'high',
                                    });
                                    getRoundedCrop(canvas)?.toBlob(
                                        (file) => {
                                            if (!file) return;
                                            resolve({
                                                file: new File([file], 'temp', {
                                                    type: 'image/png',
                                                }),
                                                url: URL.createObjectURL(file),
                                            });
                                        },
                                        'image/png',
                                        0.9,
                                    );
                                } else {
                                    cropper?.getCroppedCanvas()?.toBlob(
                                        (file) => {
                                            if (!file) return;
                                            resolve({
                                                file: new File([file], 'temp', {
                                                    type: selected?.file?.type || 'image/jpeg',
                                                }),
                                                url: URL.createObjectURL(file),
                                            });
                                        },
                                        selected?.file?.type || 'image/jpeg',
                                        0.9,
                                    );
                                }
                            });
                        }}
                    />
                </div>
            </ImageModalFooter>
        </div>
    );
}

function Controls({
    zoom,
    setZoom,
    rotation,
    setRotation,
}: {
    zoom: number;
    setZoom: any;
    rotation: number;
    setRotation: any;
}) {
    return (
        <div className="w-full flex items-center justify-between gap-x-6">
            <div className="flex-1 flex items-center justify-start gap-x-2">
                Zoom
                <Slider.StyledSlider
                    defaultValue={[zoom]}
                    max={4}
                    min={0.1}
                    step={0.05}
                    onValueChange={([val]) => setZoom(val)}
                >
                    <Slider.StyledTrack>
                        <Slider.StyledRange />
                    </Slider.StyledTrack>
                    <Slider.StyledThumb />
                </Slider.StyledSlider>
            </div>

            <div className="flex-1 flex items-center justify-end gap-x-2 [web]">
                Rotate
                <Slider.StyledSlider
                    defaultValue={[rotation]}
                    max={360}
                    min={0}
                    step={1}
                    onValueChange={([val]) => setRotation(val)}
                >
                    <Slider.StyledTrack>
                        <Slider.StyledRange />
                    </Slider.StyledTrack>
                    <Slider.StyledThumb />
                </Slider.StyledSlider>
            </div>
        </div>
    );
}

export const ImageCropper = React.forwardRef(
    (
        {
            crop,
            srcUrl,
            zoom,
            rotation,
            setCropper,
            aspect = undefined,
            setPreviousCrop,
            previousCrop,
            cropShape,
        }: {
            crop: Cropper | null;
            srcUrl?: string;
            zoom: number;
            aspect?: number;
            rotation: number;
            setCropper: React.Dispatch<React.SetStateAction<Cropper | null>>;
            setPreviousCrop: any;
            previousCrop: any;
            cropShape?: 'circle' | 'square';
        },
        ref: any,
    ) => {
        useEffect(() => {
            if (!crop) return;
            crop?.rotateTo(rotation);
        }, [rotation, crop]);

        return (
            <Cropper
                data={previousCrop}
                ref={ref}
                src={srcUrl}
                center
                autoCrop={true}
                aspectRatio={aspect}
                className={`relative max-w-[600px] max-h-[400px] mx-auto ${
                    cropShape === 'circle' ? 'circular-crop ' : ''
                }`}
                guides={false}
                background={false}
                viewMode={2}
                rotateTo={rotation}
                rotatable
                zoomTo={zoom}
                zoomOnWheel={true}
                shape={cropShape}
                responsive={true}
                checkOrientation={true}
                onInitialized={(init) => {
                    setCropper(init);
                }}
                crop={(data) => {
                    setPreviousCrop(data?.detail);
                }}
            />
        );
    },
);
ImageCropper.displayName = 'ImageCropperForwardedRef';

export function SaveAndSubmit({
    file,
    fileInput,
    externalImage,
    submitButtonText = 'Save',
    getFile,
}: {
    fileInput?: ReturnType<typeof useImageInput>['fileInput'];
    file?: TemporaryFile;
    externalImage?: ExternalImage;
    submitButtonText?: string;
    getFile?: () => Promise<TemporaryFile>;
}) {
    const { fileCreate, authorizer } = useImageModalContext();

    const onSubmit = useCallback(async () => {
        const fileUrl = fileInput?.localUrl || externalImage?.url;
        const fileName = fileInput?.file?.name || externalImage?.title || 'default';

        if (!fileUrl && !getFile) return;

        if (getFile) {
            try {
                const croppedFile = (await getFile())?.file;
                fileCreate.mutate({
                    title: croppedFile?.name || fileName,
                    file: croppedFile,
                    authorizerItem: authorizer,
                });
                return;
            } catch (error) {}
        }

        if (!!file?.url) {
            try {
                fileCreate.mutate({
                    title: fileName,
                    file: file.file,
                    authorizerItem: authorizer,
                });
                return;
            } catch (error) {}
        }

        if (!!fileInput) {
            fileCreate.mutate({
                title: fileInput.file.name,
                file: fileInput.file,
                authorizerItem: authorizer,
            });

            return;
        }

        if (!!externalImage) {
            fileCreate.mutate({
                srcUrl: externalImage?.url,
                title: externalImage?.title ?? '',
                authorizerItem: authorizer,
            });

            return;
        }
    }, [externalImage, fileInput, getFile, fileCreate, authorizer]);

    return (
        <Button size="large" type="button" disabled={fileCreate.isLoading} onClick={onSubmit}>
            {submitButtonText}
        </Button>
    );
}

export function AnimatedImageCropper<T extends { url: string; file?: File }>({
    selected,
    handlers,
    crop,
    onClickHeader,
}: {
    selected?: T;
    handlers: any;
    crop: any;
    close: any;
    onClickHeader: () => void;
}) {
    return (
        <AnimatePresence>
            {selected && (
                <motion.div layoutId={`${selected.url}`} className="absolute inset-0 bg-white flex">
                    <motion.div className="relative flex-1 max-h-full">
                        <ShowImageFromSrcURL
                            srcUrl={selected.url}
                            setCrop={handlers.setCrop}
                            croppedArea={crop}
                            crop={crop}
                            selected={selected}
                            onClickHeader={onClickHeader}
                        />
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function getRoundedCrop(sourceCanvas?: HTMLCanvasElement) {
    if (!sourceCanvas) {
        throw new Error('Expected canvas element');
    }
    sourceCanvas.style.backgroundColor = 'transparent';
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    const width = sourceCanvas.width;
    const height = sourceCanvas.height;
    canvas.width = width;
    canvas.height = height;

    context.imageSmoothingEnabled = true;
    context.drawImage(sourceCanvas, 0, 0, width, height);
    context.globalCompositeOperation = 'destination-in';
    context.beginPath();
    context.arc(width / 2, height / 2, Math.min(width, height) / 2, 0, 2 * Math.PI, true);
    context.fill();

    return canvas;
}
